/**
 * useNotifications Hook
 * Initialize and manage notification system
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../features/auth/context/AuthContext';
import { initializeFCM, requestFCMToken, forceRefreshFCMToken, isPushNotificationSupported } from '../utils/notifications/fcm';
import { isNative } from '../utils/core/platform';
import { registerNativePush, setupNativeForegroundHandler } from '../utils/notifications/nativePush';
import { NotificationBadge, ActivityTracker, showInsightNotification } from '../utils/notifications/notifications';
import { useGetInsights } from '../../features/insights/hooks/useInsightsAPI';
import { db, COLLECTIONS } from '../utils/core/firebase-config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import logger from '../utils/core/logger';
import { getUserTimezone } from '../utils/core/timezone';


/**
 * Initialize notification system on app load
 * @returns {Object} Notification state and helpers
 */
export function useNotifications() {
  const { user, loading: authLoading } = useAuth();
  const [initialized, setInitialized] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');

  // Track user activity
  useEffect(() => {
    const trackActivity = () => {
      ActivityTracker.recordActivity();
    };

    // Track on mount and every 5 minutes
    trackActivity();
    const interval = setInterval(trackActivity, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Initialize FCM
  useEffect(() => {
    const init = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      if (!user) {
        setInitialized(true);
        return;
      }

      if (isNative()) {
        // Native push via Capacitor
        try {
          const profileRef = doc(db, COLLECTIONS.NOTIFICATION_PREFERENCES, user.uid);
          const profileSnap = await getDoc(profileRef);
          // null = new user with no prefs doc → always prompt for permissions
          const pushEnabled = profileSnap.exists() ? (profileSnap.data().pushEnabled ?? null) : null;

          // Request permissions for new users (null) or users who have push enabled.
          // Skip only if they explicitly disabled push (pushEnabled === false).
          if (pushEnabled !== false) {
            const token = await registerNativePush();
            if (token) {
              setFcmToken(token);
            }
            await setupNativeForegroundHandler();
          }

          setInitialized(true);
        } catch (error) {
          logger.error('[useNotifications] Native push init failed:', error);
          setInitialized(true);
        }
        return;
      }

      if (!isPushNotificationSupported()) {
        logger.debug('[useNotifications] Push not supported');
        setInitialized(true);
        return;
      }

      try {
        await initializeFCM();

        // Check current permission
        const permission = Notification.permission;
        setPermissionStatus(permission);

        // Only request/refresh token if the user has push enabled in their preferences.
        // Without this check, reloading the app would re-activate the FCM token
        // even after the user explicitly turned notifications off.
        if (permission === 'granted') {
          const profileRef = doc(db, COLLECTIONS.NOTIFICATION_PREFERENCES, user.uid);
          const profileSnap = await getDoc(profileRef);
          const pushEnabled = profileSnap.exists() ? (profileSnap.data().pushEnabled ?? false) : false;

          if (pushEnabled) {
            // Check if FCM token needs a forced 5-day rotation.
            // A forced refresh (deleteToken + getToken) ensures the browser push
            // subscription stays fresh and deliveries don't silently fail.
            const TOKEN_REFRESH_DAYS = 5;
            let shouldForceRefresh = false;
            try {
              const tokenSnap = await getDoc(doc(db, COLLECTIONS.FCM_TOKENS, user.uid));
              if (tokenSnap.exists()) {
                const updatedAt = tokenSnap.data().updatedAt?.toDate?.();
                if (!updatedAt || (Date.now() - updatedAt.getTime()) > TOKEN_REFRESH_DAYS * 24 * 60 * 60 * 1000) {
                  shouldForceRefresh = true;
                }
              } else {
                shouldForceRefresh = true; // No token on record — force fresh registration
              }
            } catch (e) {
              // Non-critical — fall back to normal registration
            }

            const token = shouldForceRefresh
              ? await forceRefreshFCMToken()
              : await requestFCMToken();

            if (shouldForceRefresh) {
              logger.info('[useNotifications] Token force-refreshed (5-day rotation)');
            }
            setFcmToken(token);

            // Silently refresh stored timezone in case user has traveled or
            // changed their device timezone since they last saved preferences.
            const profileData = profileSnap.exists() ? profileSnap.data() : null;
            const storedTimezone = profileData?.timezone ?? null;
            const deviceTimezone = getUserTimezone();
            if (storedTimezone !== deviceTimezone) {
              try {
                await setDoc(
                  doc(db, COLLECTIONS.NOTIFICATION_PREFERENCES, user.uid),
                  { timezone: deviceTimezone, updatedAt: serverTimestamp() },
                  { merge: true }
                );
                logger.debug('[useNotifications] Timezone updated', { deviceTimezone });
              } catch (tzError) {
                // Non-critical — don't let this block initialization
                logger.warn('[useNotifications] Failed to refresh timezone:', tzError);
              }
            }
          }
        }

        setInitialized(true);
      } catch (error) {
        logger.error('[useNotifications] Initialization failed:', error);
        setInitialized(true);
      }
    };

    init();
  }, [user?.uid, authLoading]);

  /**
   * Request notification permission
   * @returns {Promise<string>} Permission status
   */
  const requestPermission = async () => {
    // On native, push is handled by nativePush.js — web FCM permission flow doesn't apply
    if (isNative()) return 'denied';
    try {
      const token = await requestFCMToken();
      setFcmToken(token);
      const permission = 'Notification' in window ? Notification.permission : 'denied';
      setPermissionStatus(permission);
      return permission;
    } catch (error) {
      logger.error('[useNotifications] Permission request failed:', error);
      setPermissionStatus('denied');
      return 'denied';
    }
  };

  return {
    initialized,
    fcmToken,
    permissionStatus,
    requestPermission,
    isSupported: isPushNotificationSupported()
  };
}

/**
 * Hook to manage notification badge count
 * @param {string} context - Insight context to monitor
 * @returns {number} Unread count
 */
export function useNotificationBadge(context = 'profile_view') {
  const [count, setCount] = useState(0);
  const { insights } = useGetInsights(context, 10, true);

  useEffect(() => {
    if (insights) {
      const unreadCount = insights.filter(i => !i.shown).length;
      setCount(unreadCount);
      NotificationBadge.set(unreadCount);
    }
  }, [insights]);

  return count;
}

/**
 * Hook to show insight notifications
 * @returns {Function} showInsightNotification
 */
export function useInsightNotifications() {
  return showInsightNotification;
}

export default {
  useNotifications,
  useNotificationBadge,
  useInsightNotifications
};
